import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { usePreview } from './PreviewContext';
import { locationService, Location } from '@/firebase/locationService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { getUserSystemRoleForOrganization } from '@/utils/roleHelpers';

interface LocationFilterContextType {
  selectedLocationIds: string[];
  availableLocations: Location[];
  isLoading: boolean;
  setSelectedLocationIds: (ids: string[]) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  loadLocations: () => Promise<void>;
}

const LocationFilterContext = createContext<LocationFilterContextType | undefined>(undefined);

export const LocationFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { previewState, isPreviewMode } = usePreview();
  const [selectedLocationIds, setSelectedLocationIdsState] = useState<string[]>([]);
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load selected location IDs from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('locationFilterIds');
    if (saved) {
      try {
        const ids = JSON.parse(saved);
        if (Array.isArray(ids) && ids.length > 0) {
          setSelectedLocationIdsState(ids);
        }
      } catch (error) {
        console.error('Error loading saved location filters:', error);
      }
    }
  }, []);

  // Save selected location IDs to localStorage whenever they change
  useEffect(() => {
    if (selectedLocationIds.length > 0) {
      localStorage.setItem('locationFilterIds', JSON.stringify(selectedLocationIds));
    } else {
      localStorage.removeItem('locationFilterIds');
    }
  }, [selectedLocationIds]);

  const loadLocations = useCallback(async () => {
    if (!currentOrganization?.id) {
      setAvailableLocations([]);
      setIsLoading(false);
      return;
    }

    // In preview mode, we need previewState; otherwise we need the real user
    if (!isPreviewMode && !user?.id) {
      setAvailableLocations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      let effectiveUserId: string;
      let userLocationIds: string[] | undefined;
      let userRole: string | undefined;

      if (isPreviewMode && previewState) {
        // Use previewed user's credentials
        if (previewState.previewType === 'user' && previewState.previewUserId) {
          effectiveUserId = previewState.previewUserId;
          userRole = previewState.role;
          userLocationIds = previewState.locationIds; // Admin role gets all regardless in getAccessibleLocations
        } else {
          // Preview by role: show all locations for support purposes
          effectiveUserId = user?.id ?? '';
          userRole = 'admin';
          userLocationIds = undefined;
        }
      } else {
        // Normal mode: use logged-in user
        effectiveUserId = user!.id;
        const userRef = doc(db, 'users', user!.id);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const userData = userSnap.data();
          const organizationId = currentOrganization.id;
          userLocationIds = userData.locationIds;
          userRole = getUserSystemRoleForOrganization(userData, organizationId) || userData.role;
        }
      }

      // Load accessible locations
      const result = await locationService.getAccessibleLocations(
        currentOrganization.id,
        effectiveUserId,
        userRole,
        userLocationIds
      );

      if (result.success && result.data) {
        const validLocations = result.data.filter(loc => loc.id && loc.name);
        setAvailableLocations(validLocations);
        
        // Validate selected location IDs - remove any that are no longer available
        setSelectedLocationIdsState(prev => {
          const validIds = prev.filter(id => validLocations.some(loc => loc.id === id));
          if (validIds.length !== prev.length) {
            // Some IDs were removed, update localStorage
            if (validIds.length > 0) {
              localStorage.setItem('locationFilterIds', JSON.stringify(validIds));
            } else {
              localStorage.removeItem('locationFilterIds');
            }
          }
          return validIds;
        });
      } else {
        setAvailableLocations([]);
        // Clear selected IDs if no locations are available
        setSelectedLocationIdsState([]);
        localStorage.removeItem('locationFilterIds');
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      setAvailableLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization?.id, user?.id, isPreviewMode, previewState?.organizationId, previewState?.previewType, previewState?.previewUserId, previewState?.role, previewState?.locationIds]);

  // Load locations when organization or user changes
  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const setSelectedLocationIds = useCallback((ids: string[]) => {
    setSelectedLocationIdsState(ids);
  }, []);

  const clearFilters = useCallback(() => {
    setSelectedLocationIdsState([]);
    localStorage.removeItem('locationFilterIds');
  }, []);

  const hasActiveFilters = selectedLocationIds.length > 0;

  const value: LocationFilterContextType = {
    selectedLocationIds,
    availableLocations,
    isLoading,
    setSelectedLocationIds,
    clearFilters,
    hasActiveFilters,
    loadLocations,
  };

  return (
    <LocationFilterContext.Provider value={value}>
      {children}
    </LocationFilterContext.Provider>
  );
};

const defaultLocationFilterValue: LocationFilterContextType = {
  selectedLocationIds: [],
  availableLocations: [],
  isLoading: false,
  setSelectedLocationIds: () => {},
  clearFilters: () => {},
  hasActiveFilters: false,
  loadLocations: async () => {},
};

export const useLocationFilter = () => {
  const context = useContext(LocationFilterContext);
  return context ?? defaultLocationFilterValue;
};

